import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowBigUp, Share2, Flame, Flag, TrendingUp, X, Gift, Swords, Coins } from "lucide-react";
import api, { TIER_META, roasterFlair, MILESTONES, flairKey } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRoastShare, ShareCardPortal } from "../hooks/useRoastShare";
import RoasterAvatar from "../components/RoasterAvatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

export default function Feed() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [progress, setProgress] = useState(null);
  const [nudgeOff, setNudgeOff] = useState(() => sessionStorage.getItem("rb_feed_nudge_off") === "1");
  const [tipTarget, setTipTarget] = useState(null);  // feed item being tipped
  const [customTip, setCustomTip] = useState("");
  const [tipping, setTipping] = useState(false);
  const [sort, setSort] = useState("top");
  const [discovery, setDiscovery] = useState(false);
  const { share, busyId, shareRef, shareItem } = useRoastShare();

  useEffect(() => { api.get("/feed/meta").then((r) => setDiscovery(r.data.discovery_enabled)).catch(() => {}); }, []);

  const sendTip = async (amount) => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    setTipping(true);
    try {
      const r = (await api.post(`/roasters/${tipTarget.roaster_id}/tip`, { amount: amt })).data;
      toast.success(`Tipped ${r.symbol}${amt} — ${tipTarget.roaster_name || "the roaster"} gets ${r.symbol}${r.net} 💸`);
      setTipTarget(null); setCustomTip("");
    } catch (e) { toast.error(e.response?.data?.detail || "Tip failed"); }
    finally { setTipping(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/feed", { params: { limit: 20, offset: 0, sort } });
      setItems(r.data.items); setHasMore(r.data.has_more);
    } finally { setLoading(false); }
  };
  const loadMore = async () => {
    const r = await api.get("/feed", { params: { limit: 20, offset: items.length, sort } });
    setItems((prev) => [...prev, ...r.data.items]); setHasMore(r.data.has_more);
  };
  useEffect(() => {
    load();
    if (user) api.get("/roaster/earnings").then((r) => setProgress(r.data)).catch(() => {});
  }, [user, sort]);

  const dareFriend = () => {
    const cap = "you're next 👀 get judged by 5 strangers on RoastLit — roastlit.app";
    if (navigator.share) navigator.share({ text: cap }).catch(() => {});
    else { navigator.clipboard?.writeText(cap)?.catch(() => {}); toast("Dare copied — paste it to tag a friend 👀"); }
  };

  const upvote = async (id) => {
    setItems((prev) => prev.map((i) => i.id === id ? { ...i, upvotes: i.upvotes + 1 } : i));
    try { await api.post(`/feed/${id}/upvote`); } catch (e) { load(); }
  };

  const startShare = (it) => share({ tier: it.tier, roast_text: it.roast_text, roaster_name: it.roaster_name, roaster_handle: it.roaster_handle, roaster_avatar: it.roaster_avatar, roaster_wins: it.roaster_wins, feedId: it.id }, it.id);

  const handleOf = (it) => it.roaster_handle || ("@" + (it.roaster_name || "roaster").replace(/[^a-zA-Z0-9]/g, "").toLowerCase().slice(0, 20));
  const initialsOf = (name) => ((name || "R").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase());

  const report = async (item) => {
    try {
      await api.post("/report", { target_type: "feed", target_id: item.id, reason: "user_report" });
      toast("Reported — pulled pending review."); load();
    } catch (e) { toast.error("Failed"); }
  };

  const RANK_ORDER = ["rising", "top", "legend"];
  const wins = progress?.accepted_count ?? 0;
  const nextRankKey = progress ? (RANK_ORDER.find((k) => MILESTONES[k].threshold > wins) || null) : null;
  const currentRankKey = progress ? ([...RANK_ORDER].reverse().find((k) => wins >= MILESTONES[k].threshold) || null) : null;
  const showNudge = progress && wins > 0 && nextRankKey && !nudgeOff;
  const shareProgress = () => {
    const floor = currentRankKey ? MILESTONES[currentRankKey].threshold : 0;
    const nt = MILESTONES[nextRankKey].threshold;
    share({
      kind: "progress", rankLabel: currentRankKey ? MILESTONES[currentRankKey].label : null,
      nextLabel: MILESTONES[nextRankKey].label, nextKey: nextRankKey, wins, nextThreshold: nt, floor,
      pct: Math.min(100, Math.round(((wins - floor) / (nt - floor)) * 100)),
      roaster_name: user?.name, roaster_avatar: user?.avatar,
    }, "feed-progress");
  };

  return (
    <div className="max-w-xl mx-auto">
      <h1 className="font-heading font-bold uppercase tracking-tighter text-4xl sm:text-5xl">The Feed</h1>
      <p className="text-zinc-400 mt-2 mb-8">The roasts people were brave enough to publish. Upvote the best burns.</p>

      {discovery && (
        <div className="flex gap-2 mb-6" data-testid="feed-discovery-tabs">
          {[["top", "Top"], ["trending", "Trending"], ["newest", "Newest"]].map(([k, label]) => (
            <button key={k} data-testid={`feed-tab-${k}`} onClick={() => setSort(k)}
              className={`px-4 py-1.5 rounded-full text-sm font-bold border transition-colors ${sort === k ? "border-brand bg-brand/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>{label}</button>
          ))}
        </div>
      )}

      {showNudge && (
        <div data-testid="feed-progress-nudge" className="animate-fade-up rounded-sm border border-brand/30 bg-brand/10 p-4 mb-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <TrendingUp className="text-brand shrink-0" size={20} />
            <div className="min-w-0">
              <div className="text-sm font-bold text-zinc-100">You're {MILESTONES[nextRankKey].threshold - wins} win{(MILESTONES[nextRankKey].threshold - wins) === 1 ? "" : "s"} from {MILESTONES[nextRankKey].label}</div>
              <div className="text-xs text-zinc-400">Flex your climb — share your progress card.</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button data-testid="feed-share-progress" onClick={shareProgress} disabled={busyId === "feed-progress"}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand hover:bg-brand-hover disabled:opacity-60 text-black font-bold text-xs px-4 py-2 transition-colors">
              <Share2 size={13} /> {busyId === "feed-progress" ? "Preparing…" : "Share progress"}
            </button>
            <button data-testid="feed-nudge-dismiss" aria-label="Dismiss" onClick={() => { setNudgeOff(true); sessionStorage.setItem("rb_feed_nudge_off", "1"); }} className="text-zinc-500 hover:text-zinc-300 p-1"><X size={16} /></button>
          </div>
        </div>
      )}

      {loading ? <div className="text-zinc-500 py-16 text-center">Loading…</div> :
        items.length === 0 ? (
          <div className="rounded-sm border border-white/10 bg-zinc-900/40 p-12 text-center">
            <Flame className="mx-auto text-zinc-700 mb-3" size={36} />
            <p className="text-zinc-400">Nothing published yet. Be the first to share a roast.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((it, i) => {
              const meta = TIER_META[it.tier];
              return (
                <div key={it.id} data-testid={`feed-item-${it.id}`} className="animate-fade-up rounded-sm border border-white/10 bg-zinc-900/60 p-6" style={{ animationDelay: `${i * 60}ms` }}>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[11px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full" style={{ color: meta.color, background: `${meta.color}1a` }}>{meta.label}</span>
                    <button data-testid={`report-feed-${it.id}`} aria-label="Report this roast" title="Report" onClick={() => report(it)} className="text-zinc-500 hover:text-brand"><Flag size={14} /></button>
                  </div>
                  <p className="text-xl leading-relaxed text-zinc-50 text-shadow-fire">"{it.roast_text}"</p>
                  <div className="flex items-center gap-2.5 mt-4">
                    <RoasterAvatar avatar={it.roaster_avatar} initials={initialsOf(it.roaster_name)} size={36} ring={meta.color} />
                    <div className="leading-tight">
                      <div className="text-sm text-zinc-100 font-semibold">{it.roaster_name || "A Roaster"}</div>
                      <div className="flex items-center gap-2">
                        <span data-testid={`feed-roaster-${it.id}`} onClick={() => it.roaster_id && navigate(`/roaster/${it.roaster_id}`)}
                          className="text-xs cursor-pointer hover:underline" style={{ color: meta.color }}>{handleOf(it)}</span>
                        {roasterFlair(it.roaster_wins) && (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ color: "#09090b", background: meta.color }}>{roasterFlair(it.roaster_wins)}</span>
                        )}
                        {it.roaster_wins > 0 && <span className="text-[11px] text-zinc-500">{it.roaster_wins} win{it.roaster_wins > 1 ? "s" : ""}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-5 pt-4 border-t border-white/10">
                    <button data-testid={`upvote-${it.id}`} onClick={() => upvote(it.id)}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/5 hover:bg-brand/20 border border-white/10 text-sm font-bold transition-colors">
                      <ArrowBigUp size={16} /> {it.upvotes}
                    </button>
                    <button data-testid={`share-${it.id}`} onClick={() => startShare(it)} disabled={busyId === it.id}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand hover:bg-brand-hover text-black text-sm font-bold transition-colors disabled:opacity-60">
                      <Share2 size={16} /> {busyId === it.id ? "Building card…" : "Share to story"}
                    </button>
                    <button data-testid={`dare-${it.id}`} onClick={dareFriend} title="Tag a friend"
                      className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-bold transition-colors">
                      <Swords size={15} /> Dare
                    </button>
                    <button data-testid={`gift-${it.id}`} onClick={() => navigate("/app/submit?gift=1")} title="Gift a friend a roast"
                      className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-bold transition-colors">
                      <Gift size={15} /> Gift them
                    </button>
                    {it.roaster_id && it.roaster_id !== user?.user_id && (
                      <button data-testid={`tip-${it.id}`} onClick={() => setTipTarget(it)} title="Tip this roaster"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#d4af37]/15 hover:bg-[#d4af37]/25 border border-[#d4af37]/30 text-[#d4af37] text-sm font-bold transition-colors">
                        <Coins size={15} /> Tip
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {hasMore && !loading && (
        <div className="flex justify-center mt-6">
          <button data-testid="feed-load-more" onClick={loadMore} className="px-6 py-2.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-sm font-bold transition-colors">Load more</button>
        </div>
      )}

      {/* offscreen render target for the exported story card */}
      <ShareCardPortal shareRef={shareRef} item={shareItem} />

      <Dialog open={!!tipTarget} onOpenChange={(o) => { if (!o) { setTipTarget(null); setCustomTip(""); } }}>
        <DialogContent data-testid="tip-dialog" className="bg-zinc-950 border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-2xl flex items-center gap-2"><Coins className="text-[#d4af37]" size={22} /> Tip {tipTarget?.roaster_name || "roaster"}</DialogTitle>
            <DialogDescription>Loved this burn? Send a tip — it goes straight to their payout balance. No strings.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-2">
            {[20, 50, 100, 200].map((amt) => (
              <button key={amt} data-testid={`tip-preset-${amt}`} onClick={() => sendTip(amt)} disabled={tipping}
                className="py-3 rounded-sm border border-[#d4af37]/40 bg-[#d4af37]/10 text-[#d4af37] font-bold hover:bg-[#d4af37]/20 transition-colors disabled:opacity-50">₹{amt}</button>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <input data-testid="tip-custom" type="number" min="1" value={customTip} onChange={(e) => setCustomTip(e.target.value)} placeholder="Custom ₹"
              className="flex-1 bg-zinc-900 border border-zinc-800 rounded-sm p-2.5 text-sm focus:border-[#d4af37] focus:outline-none" />
            <button data-testid="tip-send-custom" onClick={() => sendTip(customTip)} disabled={tipping}
              className="px-5 py-2.5 rounded-full bg-[#d4af37] hover:brightness-110 text-black font-bold transition disabled:opacity-50">{tipping ? "…" : "Tip"}</button>
          </div>
          <p className="text-[11px] text-zinc-600">Mock payment for testing — no real charge.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
